import { NodePairingViewElement } from "./pairing-view.js";
export class NodePairingGateElementImpl extends HTMLElement {
    #client;
    #migrationOwnerPublicKey;
    #running = false;
    set client(value) {
        this.#client = value;
        if (this.isConnected)
            void this.#initialize();
    }
    get client() { return this.#client; }
    showRevoked() {
        this.#running = false;
        this.#setState({ status: "revoked" });
    }
    connectedCallback() {
        if (!this.shadowRoot) {
            const root = this.attachShadow({ mode: "open" });
            root.append(document.createElement("node-pairing-view"));
            root.addEventListener("pairing-action", (event) => {
                const detail = event.detail;
                const action = detail.action;
                if (action === "recover")
                    void this.#recover();
                else if (action === "migration-begin")
                    void this.#beginMigration(detail.ownerPublicKey);
                else if (action === "migration-complete")
                    void this.#completeMigration(detail.ownerSignature);
                else
                    void this.#initialize();
            });
        }
        void this.#initialize();
    }
    async #initialize() {
        if (this.#running || this.#client === undefined)
            return;
        this.#running = true;
        this.#setState({ status: "working" });
        try {
            await this.#client.command("client-device.session.v1", { deviceType: "pwa" });
            this.#setState({ status: "ready" });
            this.dispatchEvent(new Event("pairing-ready", { bubbles: true, composed: true }));
        }
        catch (sessionError) {
            if (describeError(sessionError).toLowerCase().includes("revoked")) {
                this.#setState({ status: "revoked" });
            }
            else {
                try {
                    const result = await this.#client.command("client-device.pair.v1", {
                        deviceType: "pwa",
                    });
                    this.#setState({
                        expiresAt: result.challenge.expiresAt,
                        qrPayload: result.approval?.qrPayload ?? result.challenge.qrPayload,
                        qrModules: result.approval?.qrModules ?? result.challenge.qrModules,
                        shortCode: result.approval?.shortCode ?? result.challenge.shortCode,
                        status: "pairing",
                    });
                }
                catch (pairingError) {
                    this.#setState({ error: describeError(pairingError), status: "error" });
                }
            }
        }
        finally {
            this.#running = false;
        }
    }
    async #recover() {
        if (this.#client === undefined || this.#running)
            return;
        this.#running = true;
        try {
            await this.#client.command("client-device.recover.v1", {});
        }
        catch (error) {
            this.#setState({ error: describeError(error), status: "error" });
            this.#running = false;
            return;
        }
        this.#running = false;
        await this.#initialize();
    }
    async #beginMigration(ownerPublicKey) {
        if (this.#client === undefined || this.#running)
            return;
        const normalized = ownerPublicKey?.trim();
        if (!normalized) {
            this.#setState({ error: "Enter the existing owner's public key.", status: "error" });
            return;
        }
        this.#migrationOwnerPublicKey = normalized;
        this.#running = true;
        this.#setState({ status: "working" });
        try {
            const result = await this.#client.command("client-device.migration-begin.v1", { deviceType: "pwa", ownerPublicKey: normalized });
            this.#setState({
                expiresAt: result.challenge.expiresAt,
                qrPayload: result.challenge.qrPayload,
                qrModules: result.challenge.qrModules,
                shortCode: result.challenge.shortCode,
                status: "migration",
            });
        }
        catch (error) {
            this.#setState({ error: describeError(error), status: "error" });
        }
        finally {
            this.#running = false;
        }
    }
    async #completeMigration(ownerSignature) {
        if (this.#client === undefined || this.#running)
            return;
        const signature = ownerSignature?.trim();
        if (!this.#migrationOwnerPublicKey || !signature) {
            this.#setState({ error: "Paste the owner signature to continue.", status: "error" });
            return;
        }
        this.#running = true;
        this.#setState({ status: "working" });
        try {
            await this.#client.command("client-device.migration-complete.v1", {
                deviceType: "pwa",
                ownerPublicKey: this.#migrationOwnerPublicKey,
                ownerSignature: signature,
            });
            this.#migrationOwnerPublicKey = undefined;
        }
        catch (error) {
            this.#setState({ error: describeError(error), status: "error" });
            this.#running = false;
            return;
        }
        this.#running = false;
        await this.#initialize();
    }
    #setState(state) {
        const view = this.shadowRoot?.querySelector("node-pairing-view");
        if (view)
            view.state = state;
    }
}
function describeError(error) {
    return error instanceof Error && error.message !== "" ? error.message : "Pairing is temporarily unavailable";
}
//# sourceMappingURL=pairing-gate.js.map